import { useEffect, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { updateLepCourseImg } from '../store/api';
import {
  ADD_ANSWER,
  ADD_NEW_QUESTION,
  DELETE_LAST_ANSWER,
  DELETE_LAST_QUESTION,
  get_questions,
  SELECT_CORRECT_ANSWER,
  SET_PASSMARK,
  SET_QUESTION_PROP,
  UPDATE_ANSWER_TEXT
} from '../store/modules/questions';
import {
  BaseAnswer,
  BaseAddNewButton,
  BaseInput,
  RichTextEditor,
  BaseSelector,
  BaseDeleteButton,
  McqImageUpload
} from './';
import plus from '../assets/plus.svg';
import minus from '../assets/minus.svg';
import './css/course-questions.scss';

const questionHeight = 80;
/* we set the answer group height because when we Delete last
 answer, the height of the question group does not auto shrink,
 so we manually subtract the answer height from the full height */
const answerHeight = 106;

/* How we show/hide all questions?
  fn to show hide all question with vanilla js using the class node array to loop and
  alternate the individual question button text to +/- and expand the question items visibility
  using the .scrollHeight js method */
function toggleShowHideAll (action) {
  const list = document.getElementsByClassName('base-question');
  Array.from(list).forEach((el) => {
    const buttonImg = el.querySelector('header button img');
    if (action === 'show') {
      el.style.overflow = 'hidden';
      el.style.height = `${el.scrollHeight}px`;
      el.addEventListener('transitionend', function onEnd () {
        el.style.height = 'auto';
        el.style.overflow = 'visible';
        el.removeEventListener('transitionend', onEnd);
      });
    } else {
      el.style.transition = 'none';
      el.style.height = `${el.scrollHeight}px`;
      el.style.overflow = 'hidden';
      el.offsetHeight; // force reflow
      el.style.transition = '';
      el.style.height = `${questionHeight}px`;
    }
    buttonImg.setAttribute('src', action === 'show' ? minus : plus);
  });
}

/* How we expand/collapse a single question?
  fn to expand/collapse a single question using vanilla js and .scrollHeight method
  Note: it traverses the DOM in up direction from +/- button element to the parent element
  so this fn is dependent on the button element position in the HTML structure */
function toggleExpandQuestion (ev) {
  const buttonImg = ev.target.parentElement.querySelector('img');
  const isCollapsed = buttonImg.getAttribute('src').includes('plus');
  const questionElm = ev.target.parentNode.parentNode;
  if (isCollapsed) {
    questionElm.style.overflow = 'hidden';
    questionElm.style.height = `${questionElm.scrollHeight}px`;
    questionElm.addEventListener('transitionend', function onEnd () {
      questionElm.style.height = 'auto';
      questionElm.style.overflow = 'visible';
      questionElm.removeEventListener('transitionend', onEnd);
    });
  } else {
    questionElm.style.transition = 'none';
    questionElm.style.height = `${questionElm.scrollHeight}px`;
    questionElm.style.overflow = 'hidden';
    questionElm.offsetHeight; // force reflow
    questionElm.style.transition = '';
    questionElm.style.height = `${questionHeight}px`;
  }
  buttonImg.setAttribute('src', isCollapsed ? minus : plus);
}

function syncExpandedQuestionHeights () {
  const list = document.getElementsByClassName('base-question');
  Array.from(list).forEach((el) => {
    const buttonImg = el.querySelector('header button img');
    const isExpanded = buttonImg && buttonImg.getAttribute('src').includes('minus');
    // Only sync cards that are still in fixed-px height (mid-transition, not yet auto)
    if (isExpanded && el.style.height && el.style.height !== 'auto') {
      el.style.height = `${el.scrollHeight}px`;
    }
  });
}

/* How we display the chapter value in a question chapter selector field?
  each question object has a tag key (property) of which value represents a chapter's id,
  and we loop through chapters to get the title string value to inject into the BaseSelector
  Related chapter field */
function getChapterTitleByQtag (chapters = [], tag = '') {
  let title = '';
  tag &&
    chapters.forEach((obj) => {
      obj.id?.toString() === tag.toString() && (title = obj.title);
    });
  return title;
}

export const CourseQuestions = function () {
  const params = useParams();
  const { questions, passmark } = useSelector((state) => state.questions.data);

  const { moduleType } = useSelector((state) => state.course.data);
  const chapters = useSelector((state) => state.chapters.data);
  const chaptersList = [...chapters.map((o) => o.title), 'no chapter'];
  const dispatch = useDispatch();

  useEffect(() => {
    // this one should trigger if the course is not new
    // if the route contains /new then do not trigger this
    !window.location.pathname.includes('/new') && dispatch(get_questions(params));
  }, []);

  useLayoutEffect(() => {
    syncExpandedQuestionHeights();
  }, [questions]);

  /* How we select a Related chapter?
   * we need to update the tag property of the question in the store, so we get the value from the BaseSelector component
   * and we dispatch the proper method to update this key */
  function handleChapterSelection ({ id, key, value }) {
    // id = questionId, key = tag, value = selected value from <select> tag options
    const chapterId = chapters.reduce((acc, curr) => {
      value === curr.title && (acc = curr.id.toString());
      return acc;
    }, '');
    dispatch(SET_QUESTION_PROP({ id, key, value: chapterId }));
  }

  function handleAddAnswer ({ id, index }) {
    dispatch(ADD_ANSWER({ id }));
    const questionElm = document.getElementById(`question-${index}`);
    // Only adjust height if the card has an explicit fixed px height (not auto or unset)
    if (questionElm.style.height && questionElm.style.height !== 'auto') {
      setTimeout(() => {
        questionElm.style.height = `${questionElm.scrollHeight}px`;
      }, 0);
    }
  }

  function handleDeleteAnswer ({ id, index }) {
    dispatch(DELETE_LAST_ANSWER({ id }));
    const questionElm = document.getElementById(`question-${index}`);
    // Only adjust height if the card has an explicit fixed px height (not auto or unset)
    if (questionElm.style.height && questionElm.style.height !== 'auto') {
      questionElm.style.height = `${questionElm.scrollHeight - answerHeight}px`;
    }
  }

  async function handleQuestionImage ({ id, key, action, imageName, formData, imageLocation }) {
    if (action === 'upload') {
      dispatch(SET_QUESTION_PROP({ id, key, value: imageName }));
      await updateLepCourseImg(formData, imageLocation);
    } else {
      dispatch(SET_QUESTION_PROP({ id, key, value: null }));
      dispatch(SET_QUESTION_PROP({ id, key: `${key}Alt`, value: '' }));
    }
  }

  return (
    <section className="course-questions" cy="course-questions" id="questions">
      <header className="flex flex-between align-center">
        <div>
          <h2 cy="q-title">Questions</h2>
          <div>
            <button cy="show-all" onClick={() => toggleShowHideAll('show')}>
              Show all
            </button>
            <button cy="hide-all" onClick={() => toggleShowHideAll('hide')}>
              Hide all
            </button>
          </div>
        </div>

        {/* PASSMARK */}
        <label className="flex flex-center" htmlFor="passmark">
          <b>Passmark (%)</b>
          <input
            type="number"
            className="text-center"
            id="passmark"
            max="100"
            cy="passmark"
            value={passmark}
            onChange={(ev) => dispatch(SET_PASSMARK(ev.target.value))}
          />
        </label>
      </header>

      {questions.map(
      ({ id, tag, questionIntro, question, options, answer, explanation, questionImage, questionImageAlt, answerImage, answerImageAlt }, index) => (
          <div className="base-question m-t-24" cy="question" id={`question-${index}`} key={index}>
            <header className="flex flex-between align-center">
              <h3 className="pointer" cy="question-title" onClick={toggleExpandQuestion}>
                Question {index + 1}
              </h3>
              <button
                cy="toggle-expand"
                className="no-border bg-transparent"
                onClick={toggleExpandQuestion}
              >
                <img className="click-disabled" src={plus} alt="" />
              </button>
            </header>

            {/*Related chapter*/}
            {moduleType !== 'MCQ' && (
              <BaseSelector
                value={getChapterTitleByQtag(chapters, tag)}
                label="Related chapter"
                name="tag"
                cy="related-chapter"
                handleUpdate={(obj) => handleChapterSelection({ id, ...obj })}
                list={chaptersList}
              />
            )}

            {/* Context (optional} */}
            <RichTextEditor
              title="Context"
              name="questionIntro"
              value={questionIntro}
              questionId={id}
              onChange={(obj) => dispatch(SET_QUESTION_PROP({ key: 'questionIntro', ...obj }))}
              large
              optional
            />

            {/* Context image (optional) — LEP only */}
            {moduleType === 'LEP' && (
              <McqImageUpload
                name="questionImage"
                label="Context image"
                optional
                imageName={questionImage}
                handleImage={(obj) => handleQuestionImage({ id, ...obj })}
                altText={questionImageAlt}
                onAltTextChange={(obj) => dispatch(SET_QUESTION_PROP({ id, ...obj }))}
                imageLocation="questions"
              />
            )}

            {/* Question field */}
            <BaseInput
              name="question"
              cy="question-field"
              onInput={(obj) => dispatch(SET_QUESTION_PROP({ id, ...obj }))}
              value={question.replace(/<[^>]+>/g, '')}
              label="Question"
            />

            {/* Answers */}
            <div>
              {options?.map(({ name, value }, idx) => (
                <BaseAnswer
                  name={`answer${index}`}
                  cy="answer-combo"
                  key={idx}
                  index={idx + 1}
                  value={name}
                  answer={value}
                  correct={answer}
                  onInput={(obj) => dispatch(UPDATE_ANSWER_TEXT({ id, index: idx, ...obj }))}
                  onSelect={(obj) => dispatch(SELECT_CORRECT_ANSWER({ id, ...obj }))}
                />
              ))}

              <div className="flex flex-between m-t-16">
                <BaseAddNewButton
                  text="Add another answer"
                  cy="add-answer"
                  onClick={() => handleAddAnswer({ id, index })}
                />
                {/* Hide Delete last answer button if there are no answers */}
                {options.length ? (
                  <BaseDeleteButton
                    text="Delete last answer"
                    cy="delete-last-answer"
                    onClick={() => handleDeleteAnswer({ id, index })}
                  />
                ) : (
                  ''
                )}
              </div>
            </div>

            {/* Answer explanation */}
            <RichTextEditor
              title="Answer explanation"
              name="explanation"
              cy="answer-explanation"
              value={explanation}
              questionId={id}
              onChange={(obj) => dispatch(SET_QUESTION_PROP({ key: 'explanation', ...obj }))}
              large
            />

            {/* Explanation image (optional) — LEP only */}
            {moduleType === 'LEP' && (
              <McqImageUpload
                name="answerImage"
                label="Explanation image"
                optional
                imageName={answerImage}
                handleImage={(obj) => handleQuestionImage({ id, ...obj })}
                altText={answerImageAlt}
                onAltTextChange={(obj) => dispatch(SET_QUESTION_PROP({ id, ...obj }))}
                imageLocation="explanations"
              />
            )}
          </div>
        )
      )}

      <div className="flex flex-between m-t-24">
        <BaseAddNewButton
          name="add-new-question"
          text="Add another question"
          onClick={() => dispatch(ADD_NEW_QUESTION())}
        />
        <BaseDeleteButton
          name="delete-last-question"
          text="Delete last question"
          onClick={() => dispatch(DELETE_LAST_QUESTION())}
        />
      </div>
    </section>
  );
};
